import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { FiX, FiSend } from 'react-icons/fi';
import './SatisfactionSurvey.css';
import armaLogo from '../../assets/arma.png';

const STORAGE_KEY = 'flowsky_satisfaction_survey_v3';
const HIDE_DAYS_AFTER_SUBMIT = 30;
const DEFAULT_SURVEY_ENDPOINT = 'https://api.armabilisim.com/api/MusteriMemnuniyetAnket';
const PERSONNEL_OPTIONS = [
  'Erdal Kurtulmuş',
  'Kemal Emre Eral',
  'Sait Can Bilgeçti',
  'Mehmetcan Demir',
  'Emre Can Yelbaz',
  'Mehmet Mustafa Öncü',
];

function daysBetween(a, b) {
  const ms = Math.abs(b - a);
  return ms / (1000 * 60 * 60 * 24);
}

function safeJsonParse(value) {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

const SatisfactionSurvey = ({ mode = 'widget' }) => {
  const location = useLocation();
  const [isHidden, setIsHidden] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const [personelAd, setPersonelAd] = useState('');
  const [cozumHiziMemnuniyet, setCozumHiziMemnuniyet] = useState('');
  const [kaliciCozumDurumu, setKaliciCozumDurumu] = useState('');
  const [teknikYeterlilik, setTeknikYeterlilik] = useState('');
  const [genelPuan, setGenelPuan] = useState(''); // 1..5 string
  const [ekGorus, setEkGorus] = useState(''); // optional free text

  const surveyEndpoint = useMemo(
    () => process.env.REACT_APP_SURVEY_ENDPOINT || DEFAULT_SURVEY_ENDPOINT,
    []
  );

  useEffect(() => {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;

    const parsed = safeJsonParse(raw);
    const submittedAt = parsed?.submittedAt ? new Date(parsed.submittedAt) : null;
    if (!submittedAt || Number.isNaN(submittedAt.getTime())) return;

    const ageDays = daysBetween(submittedAt.getTime(), Date.now());
    if (ageDays <= HIDE_DAYS_AFTER_SUBMIT) {
      setIsHidden(true);
      setSubmitted(true);
    }
  }, []);

  const resetForRetry = () => {
    localStorage.removeItem(STORAGE_KEY);
    setIsHidden(false);
    setSubmitted(false);
    setPersonelAd('');
    setCozumHiziMemnuniyet('');
    setKaliciCozumDurumu('');
    setTeknikYeterlilik('');
    setGenelPuan('');
    setEkGorus('');
    setError('');
  };

  const canSubmit =
    ekGorus.trim() !== '' &&
    personelAd !== '' &&
    cozumHiziMemnuniyet !== '' &&
    kaliciCozumDurumu !== '' &&
    teknikYeterlilik !== '' &&
    genelPuan !== '' &&
    !isSubmitting;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!canSubmit) {
      setError('Lütfen zorunlu soruların tamamını yanıtlayın.');
      return;
    }

    // Backend DTO ile birebir uyumlu body (fazla alan göndermiyoruz).
    const dto = {
      cozumHiziMemnuniyet,
      kaliciCozumDurumu,
      teknikYeterlilik,
      // Soru 6 kaldırıldı; backend alanı null/boş kalmasın diye sabit bir değer gönderiyoruz.
      destekIhtiyacKarsilama: 'Belirtilmedi',
      // Backend DTO şu an `genelPuan` alanını string bekliyor (400'de conversion error geliyor).
      genelPuan: String(genelPuan),
      ekGorus: ekGorus.trim(),
      personelAd,
    };

    const toStore = {
      ...dto,
      page: location?.pathname || '/',
      submittedAt: new Date().toISOString(),
    };

    setIsSubmitting(true);
    try {
      const res = await fetch(surveyEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dto),
      });

      if (!res.ok) {
        const message = (await res.text())?.trim();
        setError(message || 'Gönderim sırasında bir hata oluştu. Lütfen tekrar deneyin.');
        return;
      }

      localStorage.setItem(STORAGE_KEY, JSON.stringify(toStore));
      setSubmitted(true);
      setIsHidden(true);
    } catch (err) {
      setError(
        'Gönderim sırasında bir hata oluştu. Lütfen internet bağlantınızı kontrol edip tekrar deneyin.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isHidden && mode !== 'page') {
    // İsterseniz tamamen gizlemek yerine küçük bir teşekkür rozetine çevirebiliriz.
    return null;
  }

  return (
    <div className={`survey-root ${mode === 'page' ? 'survey-root-page' : ''}`}>
      <div className={mode === 'page' ? 'survey-modal survey-modal-page' : 'survey-modal'}>
        <div className="survey-brand" aria-label="Arma Bilişim">
          <img src={armaLogo} alt="Arma Bilişim" loading="lazy" />
        </div>
        {mode !== 'page' && (
          <div className="survey-header">
            <div>
              <div className="survey-title">Memnuniyet Anketi</div>
              <div className="survey-subtitle">2 dakikadan kısa sürer.</div>
            </div>
            <button type="button" className="survey-icon-btn" onClick={() => window.history.back()} aria-label="Kapat">
              <FiX size={18} />
            </button>
          </div>
        )}

        <form className="survey-form" onSubmit={onSubmit}>
          {isHidden && (
            <div className="survey-success">
              <div className="survey-success-text">Teşekkürler! Anketiniz kaydedildi.</div>
              <button type="button" className="survey-retry" onClick={resetForRetry}>
                Tekrar gönder
              </button>
            </div>
          )}

          {!isHidden && (
            <>
              <div className="survey-field">
                <label className="survey-label" htmlFor="survey-q6">
                  Soru 1: Cari adınız:
                </label>
                <textarea
                  id="survey-q6"
                  className="survey-textarea"
                  rows={4}
                  value={ekGorus}
                  onChange={(e) => setEkGorus(e.target.value)}
                  placeholder="Cari adınız giriniz..."
                />
              </div>

              <div className="survey-field">
                <div className="survey-label">
                  Soru 2: Talebinizle ilgilenen personelin teknik bilgisini ve konuya hakimiyetini nasıl değerlendirirsiniz?
                </div>

                <label className="survey-label" htmlFor="survey-personel" style={{ marginTop: 4 }}>
                  Personel seçiniz
                </label>
                <select
                  id="survey-personel"
                  className="survey-input"
                  value={personelAd}
                  onChange={(e) => setPersonelAd(e.target.value)}
                >
                  <option value="" disabled>
                    Seçiniz
                  </option>
                  {PERSONNEL_OPTIONS.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>

                <div className="survey-options" role="radiogroup" aria-label="Personel teknik bilgi değerlendirmesi">
                  {['Çok Başarılı', 'Yeterli', 'Orta', 'Yetersiz', 'Çok Yetersiz'].map((label) => (
                    <label key={label} className={`survey-option ${teknikYeterlilik === label ? 'active' : ''}`}>
                      <input
                        type="radio"
                        name="teknikYeterlilik"
                        value={label}
                        checked={teknikYeterlilik === label}
                        onChange={(e) => setTeknikYeterlilik(e.target.value)}
                      />
                      <span className="survey-option-text">{label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="survey-field">
                <div className="survey-label">Soru 3: Sorununuzun çözüm hızından ne kadar memnunsunuz?</div>
                <div className="survey-options" role="radiogroup" aria-label="Çözüm hızı memnuniyeti">
                  {[
                    'Çok Memnunum',
                    'Memnunum',
                    'Orta',
                    'Memnun Değilim',
                    'Hiç Memnun Değilim',
                  ].map((label) => (
                    <label
                      key={label}
                      className={`survey-option ${cozumHiziMemnuniyet === label ? 'active' : ''}`}
                    >
                      <input
                        type="radio"
                        name="cozumHiziMemnuniyet"
                        value={label}
                        checked={cozumHiziMemnuniyet === label}
                        onChange={(e) => setCozumHiziMemnuniyet(e.target.value)}
                      />
                      <span className="survey-option-text">{label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="survey-field">
                <div className="survey-label">
                  Soru 4: Sorununuzun kalıcı olarak çözüldüğünü düşünüyor musunuz?
                </div>
                <div className="survey-options" role="radiogroup" aria-label="Kalıcı çözüm değerlendirmesi">
                  {[
                    'Kesinlikle Evet',
                    'Evet, şu an için sorun görünmüyor',
                    'Kısmen çözüldü / Geçici bir çözüm sağlandı',
                    'Hayır, sorun devam ediyor',
                  ].map((label) => (
                    <label key={label} className={`survey-option ${kaliciCozumDurumu === label ? 'active' : ''}`}>
                      <input
                        type="radio"
                        name="kaliciCozumDurumu"
                        value={label}
                        checked={kaliciCozumDurumu === label}
                        onChange={(e) => setKaliciCozumDurumu(e.target.value)}
                      />
                      <span className="survey-option-text">{label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="survey-field">
                <div className="survey-label">
                  Soru 5: Genel olarak aldığınız Arma Bilişim hizmetlerine 1 ile 5 arasında kaç puan verirsiniz?
                </div>
                <div className="survey-options" role="radiogroup" aria-label="Genel puan 1-5">
                  {[
                    { value: '1', label: '1 (Zayıf)' },
                    { value: '2', label: '2' },
                    { value: '3', label: '3' },
                    { value: '4', label: '4' },
                    { value: '5', label: '5 (Mükemmel)' },
                  ].map((opt) => (
                    <label key={opt.value} className={`survey-option ${genelPuan === opt.value ? 'active' : ''}`}>
                      <input
                        type="radio"
                        name="genelPuan"
                        value={opt.value}
                        checked={genelPuan === opt.value}
                        onChange={(e) => setGenelPuan(e.target.value)}
                      />
                      <span className="survey-option-text">{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {error && <div className="survey-error">{error}</div>}

              <div className="survey-actions">
                {mode !== 'page' && (
                  <button type="button" className="survey-secondary" onClick={() => window.history.back()}>
                    Vazgeç
                  </button>
                )}
                <button type="submit" className="survey-primary" disabled={!canSubmit}>
                  <FiSend size={16} />
                  {isSubmitting ? 'Gönderiliyor...' : 'Gönder'}
                </button>
              </div>

              {submitted && !isHidden && (
                <button type="button" className="survey-retry" onClick={resetForRetry}>
                  Tekrar gönder
                </button>
              )}
            </>
          )}
        </form>
      </div>
    </div>
  );
};

export default SatisfactionSurvey;

